// Share-slip helpers — encode/decode parlay legs to a compact URL fragment.
// Design goal: a 2–8 leg slip must fit comfortably in an SMS-safe URL.

const SLIP_PARAM = "slip";

/** Encode a legs array to a base64url string. */
export function encodeSlip(legs) {
  if (!legs || legs.length === 0) return "";
  try {
    const compact = legs.map((l) => ({
      s: l.sport, h: l.home_team, a: l.away_team,
      d: l.date || null, t: l.time || null,
      p: l.side === "home" ? "h" : "a",
    }));
    const json = JSON.stringify(compact);
    // base64url-encode (replace +/= so the URL stays safe)
    const b64 = btoa(unescape(encodeURIComponent(json)));
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch { return ""; }
}

/** Decode a base64url string back to a legs array. Returns [] on any failure. */
export function decodeSlip(encoded) {
  if (!encoded || typeof encoded !== "string") return [];
  try {
    const b64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    // Re-add padding if needed
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(escape(atob(padded)));
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((l) => l && typeof l === "object" && l.s && l.h && l.a && (l.p === "h" || l.p === "a"))
      .slice(0, 8)
      .map((l) => ({
        sport: String(l.s),
        home_team: String(l.h),
        away_team: String(l.a),
        date: l.d ? String(l.d) : null,
        time: l.t ? String(l.t) : null,
        side: l.p === "h" ? "home" : "away",
      }));
  } catch { return []; }
}

/** Read `?slip=…` from the current URL. */
export function readSlipFromUrl() {
  if (typeof window === "undefined") return [];
  try {
    const p = new URLSearchParams(window.location.search);
    return decodeSlip(p.get(SLIP_PARAM) || "");
  } catch { return []; }
}

/** Build the shareable `/?slip=…` URL. */
export function buildSlipUrl(legs) {
  const encoded = encodeSlip(legs);
  if (!encoded) {
    return typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : "";
  }
  const base = `${window.location.origin}${window.location.pathname}`;
  return `${base}?${SLIP_PARAM}=${encoded}`;
}

export { SLIP_PARAM };
